import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  Container,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from "@mui/material";
import axiosInstance from "../../axios/axiosInstance";
import { toast } from "react-toastify";
import {
  employmentTypes,
  companyTypes,
  workEnvironments,
  roleOpenOptions,
  skillLevels,
  experienceLevels, // ✅ assuming this exists in constants
} from "./Constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const ReviewCreateJob = ({
  formData,
  skillsData,
  selectedSoftSkills,
  functions,
  roles,
  countries,
  softSkillNames,
  onBack,
  onPublish,
  interviewStages = [],
  jobId,
  isEditMode = false,
}) => {
  const navigate = useNavigate();
  const [publishing, setPublishing] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState("");

  // Helper functions to get names from IDs
  const getFunctionName = () => {
    const func = functions?.find((f) => f.id === formData.functions);
    return func?.name || formData.functions;
  };

  const getRoleName = () => {
    const role = roles?.find((r) => r.id === formData.position);
    return role?.name || formData.position;
  };

  const getCountryName = () => {
    const country = countries?.find((c) => c.id === formData.countries);
    return country?.name || formData.countries;
  };

  // Get label from constant array by value
  const getLabel = (options, value) =>
    options.find((opt) => opt.value === value)?.label || value;

  // Group skills by category
  const groupedSkills = {
    must: [],
    should: [],
    could: [],
  };

  Object.entries(skillsData || {}).forEach(([key, data]) => {
    const [category, mainSkill] = key.split("|");
    groupedSkills[category]?.push({
      mainSkill,
      level: data.level,
      subSkills: data.subSkills || [],
    });
  });

  const buildPayload = (category) => {
    const skills = [];

    groupedSkills[category].forEach(({ mainSkill, level, subSkills }) => {
      const formattedSubSkills = (subSkills || []).map((sub) => {
        const subName = typeof sub === "string" ? sub : sub.name;
        const subLevel = typeof sub === "string" ? level : sub.level;
        return {
          name: subName,
          level:
            skillLevels[subLevel - 1] || skillLevels[level - 1] || "Beginner",
        };
      });

      skills.push({
        [mainSkill]: {
          level: skillLevels[level - 1],
          subSkills: formattedSubSkills,
        },
      });
    });

    return { skills, specificSkills: [] };
  };

  const createJobPosting = async (status) => {
    const mustHave = buildPayload("must");
    const shouldHave = buildPayload("should");
    const couldHave = buildPayload("could");

    // Transform languages to API format
    const languageRequirements =
      formData.languages && formData.languages.length > 0
        ? formData.languages.map((lang) => ({
          language: lang.name,
          fluency: lang.fluency,
        }))
        : [];

    // Build skills payload
    const skillsPayload = {
      job_role_skills_id: formData.position,
      selected_must_have_skills: mustHave.skills,
      selected_must_have_specific_skills: mustHave.specificSkills,
      selected_should_have_skills: shouldHave.skills,
      selected_should_have_specific_skills: shouldHave.specificSkills,
      selected_could_have_skills: couldHave.skills,
      selected_could_have_specific_skills: couldHave.specificSkills,
    };

    if (isEditMode && jobId) {
      // Update existing job posting
      const updatePayload = {
        job_function_id: formData.functions,
        job_role_id: formData.position,
        country_id: formData.countries,
        status: status,
        is_confidential: formData.confidential,
        selected_soft_skills: selectedSoftSkills,
        summary: formData.summary,
        experience_level: formData.experienceLevel,
        employment_type: formData.employmentType,
        role_open_reason: formData.whyRoleOpen,
        why_join_team: formData.whyJoinTeam,
        company_type: formData.typeOfCompany,
        work_environment: formData.workEnvironment,
        language_requirements: languageRequirements,
        skills: skillsPayload,
        interviewStages: interviewStages,
      };

      await axiosInstance.put(`api/job-postings/${jobId}`, updatePayload);
      return { id: jobId };
    } else {
      // Create new job posting
      const jobPostingRes = await axiosInstance.post(`api/job-postings`, {
        job_function_id: formData.functions,
        job_role_id: formData.position,
        country_id: formData.countries,
        status: status,
        is_confidential: formData.confidential,
        selectedSoftSkills: selectedSoftSkills,
        summary: formData.summary,
        experience_level: formData.experienceLevel,
        employment_type: formData.employmentType,
        role_open_reason: formData.whyRoleOpen,
        why_join_team: formData.whyJoinTeam,
        company_type: formData.typeOfCompany,
        work_environment: formData.workEnvironment,
        languageRequirements: languageRequirements,
        interviewStages: interviewStages,
      });

      const jobPosting = jobPostingRes.data;

      // Add skills to job posting
      await axiosInstance.post(
        `api/job-postings/${jobPosting.id}/skills`,
        skillsPayload,
      );

      return jobPosting;
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setError("");

    try {
      await createJobPosting("published");
      toast.success(
        isEditMode
          ? "Job posting updated and published successfully!"
          : "Job posting published successfully!",
      );
      onPublish();
    } catch (err) {
      setError(
        err.message || "Failed to publish job posting. Please try again.",
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    setError("");

    try {
      await createJobPosting("draft");
      toast.success(
        isEditMode
          ? "Job posting saved as draft successfully!"
          : "Job posting saved as draft successfully!",
      );
      onPublish();
    } catch (err) {
      setError(
        err.message || "Failed to save job posting as draft. Please try again.",
      );
    } finally {
      setSavingDraft(false);
    }
  };

  const renderSkillCategory = (category, label) => {
    if (groupedSkills[category].length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {label}
        </Typography>
        {groupedSkills[category].map(({ mainSkill, level, subSkills }) => (
          <Box key={mainSkill} sx={{ mb: 2, pl: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {mainSkill} - {skillLevels[level - 1]}
            </Typography>
            {subSkills.length > 0 && (
              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {subSkills.map((sub, idx) => {
                  const subName = typeof sub === "string" ? sub : sub.name;
                  const subLevel = typeof sub === "string" ? level : sub.level;
                  const subLevelText =
                    typeof subLevel === "number"
                      ? skillLevels[subLevel - 1]
                      : subLevel;

                  return (
                    <Chip
                      key={idx}
                      label={`${subName} (${subLevelText})`}
                      size="small"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ mt: 6 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/corporate/job-postings")}
        sx={{ mb: 2 }}
      >
        Back to Job Postings
      </Button>
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Stepper activeStep={4} alternativeLabel sx={{ mb: 4 }}>
          <Step>
            <StepLabel>Create a role</StepLabel>
          </Step>
          <Step>
            <StepLabel>Set skills</StepLabel>
          </Step>
          <Step>
            <StepLabel>Company type & culture Fit</StepLabel>
          </Step>
          <Step>
            <StepLabel>Define interview process</StepLabel>
          </Step>
          <Step>
            <StepLabel>Review & Publish</StepLabel>
          </Step>
        </Stepper>

        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {isEditMode ? "Review & Update Job Details" : "Review Job Details"}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          {isEditMode
            ? "Review and update all details before republishing your job."
            : "Review all details before publishing your job."}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Basic Information */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Function
            </Typography>
            <Typography variant="body1">{getFunctionName()}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Position
            </Typography>
            <Typography variant="body1">{getRoleName()}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Country
            </Typography>
            <Typography variant="body1">{getCountryName()}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Experience Level
            </Typography>
            <Typography variant="body1">
              {getLabel(experienceLevels, formData.experienceLevel)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Employment Type
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
              {formData.employmentType.map((type) => (
                <Chip
                  key={type}
                  label={getLabel(employmentTypes, type)}
                  size="small"
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Why Role Open
            </Typography>
            <Typography variant="body1">
              {getLabel(roleOpenOptions, formData.whyRoleOpen)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Company Type
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 0.5,
                  minWidth: 0,
                }}
              >
                {formData.typeOfCompany.map((type) => (
                  <Chip
                    key={type}
                    label={getLabel(companyTypes, type)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Work Environment
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 0.5,
                  minWidth: 0,
                }}
              >
                {formData.workEnvironment.map((type) => (
                  <Chip
                    key={type}
                    label={getLabel(workEnvironments, type)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Job Summary
            </Typography>
            <Typography
              variant="body1"
              sx={{
                wordBreak: "break-word",
                maxHeight: 250,
                overflowY: "auto",
                p: 1.5,
                mt: 0.5,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {formData.summary || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Why Join Team
            </Typography>
            <Typography
              variant="body1"
              sx={{
                wordBreak: "break-word",
                maxHeight: 250,
                overflowY: "auto",
                p: 1.5,
                mt: 0.5,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {formData.whyJoinTeam || "N/A"}
            </Typography>
          </Grid>
        </Grid>

        {/* Languages Section */}
        {formData.languages && formData.languages.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Language Requirements
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
              {formData.languages.map((lang, index) => (
                <Chip
                  key={index}
                  label={`${lang.name} (${lang.fluency})`}
                  size="small"
                />
              ))}
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Soft Skills Section */}
        {selectedSoftSkills.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Selected Soft Skills
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
              {softSkillNames
                .filter((skill) => selectedSoftSkills.includes(skill.id))
                .map((skill) => (
                  <Chip
                    key={skill.id}
                    label={skill.name}
                    size="small"
                    title={skill.description} // optional: shows description on hover
                  />
                ))}
            </Box>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* Skills Section */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Selected Technical Skills
        </Typography>

        {renderSkillCategory("must", "Must Have Skills")}
        {renderSkillCategory("should", "Should Have Skills")}
        {renderSkillCategory("could", "Could Have Skills")}

        <Divider sx={{ my: 3 }} />

        {/* Interview Process Section */}
        {interviewStages.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Interview Process
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {interviewStages.map((stage, index) => (
                <Grid item xs={12} key={index}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      sx={{ fontWeight: 600, minWidth: 30 }}
                    >
                      {index + 1}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Stage Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {stage.stageName}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Interviewers
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {Array.isArray(stage.interviewers)
                          ? stage.interviewers.join(", ")
                          : stage.interviewer}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onBack}
            disabled={publishing || savingDraft}
          >
            Back
          </Button>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleSaveDraft}
              disabled={publishing || savingDraft}
            >
              {savingDraft ? <CircularProgress size={24} /> : "Save as Draft"}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePublish}
              disabled={publishing || savingDraft}
            >
              {publishing ? (
                <CircularProgress size={24} />
              ) : isEditMode ? (
                "Update & Publish"
              ) : (
                "Publish Job"
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ReviewCreateJob;
